import { useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useQuery } from "react-query";

import styles from "./BookingDetails.module.css";

import { FaTrash } from 'react-icons/fa';
import { toast } from "react-toastify";
import {
  ArrowLeft,
  CalendarClock,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  CreditCard,
  FileSignature,
  Link as LinkIcon,
  Mail,
  MapPin,
  MessageSquare,
  Pencil,
  Plus,
  Phone,
  Send,
  ShieldCheck,
  User,
  XCircle,
} from "lucide-react";
import SlotModal from "../../components/slotModal/slotModal.js";
import SlotTableRow from "../../components/slotTableRow/slotTableRow.js";
import EditModal from "../../components/editModal/editModal.js";
import ContractModalContent from "../../components/contractModalContent/contractModalContent.js";
import NotesForm from "../../components/forms/notesForm.js";
import ArrivalTimeForm from "../../components/forms/arrivalTimeForm.js";
import CustomerForm from "../../components/forms/customerForm.js";
import ChecklistForm from "../../components/forms/checklistForm.js";
import LicenseForm from "../../components/forms/licenseForm.js";

import { useBooking } from "../../hooks/useBooking.js";
import { usePayment } from "../../hooks/usePayment.js";
import { useSlots } from "../../hooks/useSlots.js";
import { useModal } from "../../hooks/useModal.js";
import { useCustomer } from "../../hooks/useCustomer.js";
import { useClient } from "../../hooks/useClient.js";
import ExtraForm from "../../components/forms/extraForm.js";
import * as helpers from "../../utils/utils.js";

const formatTimestamp = (value) => {
  if (!value) return "Not recorded";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not recorded";

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatMoney = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

const formatChargeLabel = (value) => {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const CUSTOMER_DETAIL_KEYS = new Set([
  "firstName",
  "lastName",
  "email",
  "phone",
  "address",
  "city",
  "state",
  "zip",
]);

const formatCustomerValue = (value) => {
  if (value === null || value === undefined || value === "") return "Not recorded";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const getCustomerDetails = (customer) =>
  Object.entries(customer || {})
    .filter(([key]) => !CUSTOMER_DETAIL_KEYS.has(key))
    .filter(([, value]) => value !== null && value !== undefined && value !== "");

const sanitizeBookingSlots = (slots = []) =>
  slots.map(({ price, unit, ...slot }) => slot);

const toBookingUpdateData = (booking, overrides = {}) => {
  const bookingData = { ...(booking || {}) };
  const cleanOverrides = { ...(overrides || {}) };

  delete bookingData.id;
  delete bookingData.customer;
  delete cleanOverrides.id;
  delete cleanOverrides.customer;

  const slots = cleanOverrides.slots || bookingData.slots || [];

  return {
    ...bookingData,
    ...cleanOverrides,
    slots: sanitizeBookingSlots(slots),
  };
};

function StatusCard({ icon: Icon, label, value, tone = "neutral", onClick, disabled = false }) {
  const StatusIcon = tone === "good" ? CheckCircle2 : tone === "bad" ? XCircle : Icon;
  const Component = onClick ? "button" : "div";

  return (
    <Component
      className={`${styles.statusCard} ${onClick ? styles.statusAction : ""} ${styles[tone] || ""}`}
      onClick={onClick}
      disabled={onClick ? disabled : undefined}
      type={onClick ? "button" : undefined}
    >
      <div className={styles.statusIcon}>
        <StatusIcon size={18} aria-hidden="true" />
      </div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
    </Component>
  );
}

const BookingDetailsPage = () => {
  const { startSquarePOSPayment } = usePayment()
  const {
    updateBooking,
    getBookingById,
    chargeSavedCard,
    ensureSquareCustomer,
    getChecklistTemplates,
    getBookingChecklists,
    createBookingChecklist,
    updateBookingChecklist,
    emailConfirmation,
    emailContracts,
    emailCheckin,
    emailReviewRequest,
    emailLicenseRequirement,
  } = useBooking();
  const { updateSlot } = useSlots();
  const { retrieveCustomerById, retrieveCustomerCards, updateCustomer } = useCustomer();
  const { clientData } = useClient();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get("booking_id");

  const { show: showSlotModal, close: hideSlotModal, open: openSlotModal } = useModal();
  const { show: showContract, close: hideContract, open: openContract } = useModal();
  const { show: showCustomer, close: hideCustomer, open: openCustomer } = useModal();
  const { show: showPayment, close: hidePayment, open: openPayment } = useModal();
  const { show: showNote, close: hideNote, open: openNote } = useModal();
  const { show: showArrivalModal, close: closeArrivalModal, open: openArrivalModal } = useModal();
  const { show: showEmailModal, close: hideEmailModal, open: openEmailModal } = useModal();
  const { show: showLinkModal, close: hideLinkModal, open: openLinkModal } = useModal();
  const { show: showChecklist, close: hideChecklist, open: openChecklist } = useModal();
  const { show: showLicenseModal, close: closeLicenseModal, open: openLicenseModal } = useModal();
  const {
    show: showSavedCardConfirm,
    close: closeSavedCardConfirm,
    open: openSavedCardConfirm,
  } = useModal();

  const [editSlots, setEditSlots] = useState(false);
  const [selectedPayments, setSelectedPayments] = useState(new Set());
  const [selectedPaymentModalData, setSelectedPaymentModalData] = useState({});
  const [selectedPaymentModalKey, setSelectedPaymentModalKey] = useState("");
  const [paymentModalRenderKey, setPaymentModalRenderKey] = useState(0);
  const initializedSelectionRef = useRef(false);

  const {
      data: booking,
      error: bookingError,
      isLoading: bookingIsLoading,
      refetch: refetchBooking,
    } = useQuery(["bookingData", bookingId], () => getBookingById(bookingId), {
      staleTime: 1000 * 60 * 30,          // 30 minutes
      refetchOnWindowFocus: false,        // prevent re-fetch on tab switch
      cacheTime: 1000 * 60 * 60,          // 1 hour cache
    });

  useEffect(() => {
    initializedSelectionRef.current = false;
  }, [bookingId]);

  useEffect(() => {
    if (booking?.costs && !initializedSelectionRef.current) {
      const unpaidKeys = Object.entries(booking.costs)
        .filter(([, value]) => Number(value.amount ?? 0) - Number(value.paid ?? 0) > 0)
        .map(([key]) => key);
      setSelectedPayments(new Set(unpaidKeys));
      initializedSelectionRef.current = true;
    }
  }, [booking]);

  const {
      data: customer,
      error: customerError,
      isLoading: customerIsLoading,
  } = useQuery(["customerData", booking?.customerId], () => retrieveCustomerById(booking?.customerId),
      {
        enabled: !!booking?.customerId, // only run when booking.customerId exists{
        staleTime: 1000 * 60 * 30,          // 30 minutes
        refetchOnWindowFocus: false,        // prevent re-fetch on tab switch
        cacheTime: 1000 * 60 * 60,          // 1 hour cache
      });

  const { data: customerCards } = useQuery(
    ["customerCards", booking?.customerId],
    () => retrieveCustomerCards(booking?.customerId),
    {
      enabled: !!booking?.customerId,
      staleTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
      cacheTime: 1000 * 60 * 60,
    });

  const hasCardOnFile = Array.isArray(customerCards) && customerCards.length > 0;

  const {
    data: checklistTemplates = [],
    isLoading: checklistTemplatesIsLoading,
    error: checklistTemplatesError,
  } = useQuery(
    ["checklistTemplates", "active"],
    () => getChecklistTemplates({ active: true }),
    {
      staleTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
      cacheTime: 1000 * 60 * 60,
    }
  );

  const {
    data: bookingChecklists = [],
    isLoading: bookingChecklistsIsLoading,
    error: bookingChecklistsError,
  } = useQuery(
    ["bookingChecklists", bookingId],
    () => getBookingChecklists(bookingId),
    {
      enabled: !!bookingId,
      staleTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
      cacheTime: 1000 * 60 * 60,
    }
  );
  
  
  const handleSendConfimrationEmail = async () => {
    toast.promise(
      emailConfirmation.mutateAsync(bookingId),
      {
        pending: 'Sending confirmation email...',
        success: 'Confirmation email sent successfully!',
        error: 'Failed to send confirmation email.'
      }
    );
  };

  const handleSendContractEmail = async () => {
    toast.promise(
      emailContracts.mutateAsync(bookingId),
      {
        pending: 'Sending contracts email...',
        success: 'Contract email sent successfully!',
        error: 'Failed to send contracts email.'
      }
    );
  };

  const handleSendCheckinEmail = async () => {
    toast.promise(
      emailCheckin.mutateAsync(bookingId),
      {
        pending: 'Sending checkin email...',
        success: 'Checkin email sent successfully!',
        error: 'Failed to send checkin email.'
      }
    );
  };  
  
  const handleSendReviewRequestEmail = async () => {
    toast.promise(
      emailReviewRequest.mutateAsync(booking.customerId),
      {
        pending: 'Sending review request email...',
        success: 'Review request email sent successfully!',
        error: 'Failed to send review request email.'
      }
    );
  };

    const handleSendLicenseRequirementEmail = async () => {
    toast.promise(
      emailLicenseRequirement.mutateAsync(booking.customerId),
      {
        pending: 'Sending license requirement email...',
        success: 'License requirement email sent successfully!',
        error: 'Failed to send license requirement email.'
      }
    );
  };

  const handleCopyText = (link) => {
    navigator.clipboard.writeText(link);
    toast.success("Link copied to clipboard");
  }


  const handleUpdateArrival = async (time) => {
    if (time) {
      try {
        await toast.promise(
          updateBooking({
            id: bookingId,
            bookingData: toBookingUpdateData(booking, { arrivalTime: time }),
          }),
          {
            pending: "Saving arrival time...",
            success: "Arrival time saved.",
            error: "Failed to save arrival time.",
          }
        );
        await refetchBooking();
        closeArrivalModal();
      } catch (error) {
        console.error("Error updating booking arrival time:", error);
      }
    }
  };

  const handleSaveLicense = async (licenseChecked) => {
    const hasLicense = Boolean(licenseChecked);
    const updatedBooking = {
      ...booking,
      hasLicense,
      hasLicenseTimestamp: hasLicense
        ? booking.hasLicenseTimestamp || new Date().toISOString()
        : null,
      hasLicenseIp: hasLicense ? booking.hasLicenseIp || "admin" : null,
    };

    try {
      await toast.promise(
        updateBooking({
          id: bookingId,
          bookingData: toBookingUpdateData(booking, updatedBooking),
        }),
        {
          pending: "Saving license status...",
          success: "License status updated successfully!",
          error: "Failed to update license status.",
        }
      );

      await refetchBooking();
      closeLicenseModal();
    } catch (error) {
      console.error("Error updating booking license requirement:", error);
    }
  };

  const handleChecklistSubmit = async (checklistData) => {
    const currentChecklist = bookingChecklists?.[0];

    try {
      await toast.promise(
        currentChecklist?.id
          ? updateBookingChecklist.mutateAsync({
              id: bookingId,
              checklistId: currentChecklist.id,
              checklistData,
            })
          : createBookingChecklist.mutateAsync({
              id: bookingId,
              checklistData,
            }),
        {
          pending: "Saving checklist...",
          success: "Checklist saved successfully!",
          error: {
            render({ data }) {
              return data?.response?.data?.message || "Failed to save checklist.";
            },
          },
        }
      );

      hideChecklist();
    } catch (error) {
      console.error("Error saving checklist:", error);
    }
  };
  


  const handleAddSlot = async (slot) => {
    if (slot) {

      const { id, ...updatedSlot } = {
        ...slot,
        status: "booked",
      };

      try {
        const result = await updateSlot({
          id: id, 
          slotData: {...updatedSlot, bookingId: bookingId}
        });
        console.log('Slot updated:', result);
      } catch (error) {
        console.error('Error updating slot:', error);
      }

      const updatedSlots = [
        ...(booking.slots || []),
        { ...updatedSlot, id: id },
      ].sort((a, b) => a.date - b.date);

      try {
        await toast.promise(
          updateBooking({
            id: bookingId,
            bookingData: toBookingUpdateData(booking, { slots: updatedSlots }),
          }),
          {
            pending: "Adding slot to booking...",
            success: "Slot added to booking.",
            error: "Failed to add slot to booking.",
          }
        );
        await refetchBooking();
      } catch (error) {
        console.error('Error updating booking:', error);
      }

    }
  }

const handleRemoveSlot = async (slot) => {
  if (!slot) return;

  const { id, bookingId: thisBookingsId, ...rest } = slot;
  const updatedSlot = { ...rest, status: "available" };

  try {
    const slotResult = await updateSlot({ id, slotData: updatedSlot, bookingId: bookingId });
    console.log("Slot updated:", slotResult);

    // Clone booking so we don't mutate state
    const updatedBooking = {
      ...booking,
      slots: booking.slots.filter((s) => s.id !== id),
      removedSlots: [...(booking.removedSlots || []), id],
    };

    const bookingResult = await updateBooking({
      id: bookingId,
      bookingData: toBookingUpdateData(booking, updatedBooking),
    });
    await refetchBooking();
    console.log("Booking updated:", bookingResult);
  } catch (error) {
    console.error("Error removing slot:", error);
  }
};


  const handleUpdateBookingPayment = async (paymentData, key) => {
    const paymentKey = String(key || "").trim();

    if (!paymentKey) {
      toast.error("Payment field name is required.");
      return;
    }

    const amount = Number(paymentData.amount ?? 0);
    const paid = Number(paymentData.paid ?? 0);

    if (!Number.isFinite(amount) || !Number.isFinite(paid)) {
      toast.error("Payment amount and paid values must be valid numbers.");
      return;
    }

    const sanitizedPaymentData = {
      ...paymentData,
      amount,
      paid,
      payment: paymentData.payment || {},
    };
    delete sanitizedPaymentData.name;

    const updatedCosts = {
      ...(booking.costs || {}),
      [paymentKey]: sanitizedPaymentData,
    };

    try {
      await toast.promise(
        updateBooking({
          id: bookingId,
          bookingData: toBookingUpdateData(booking, { costs: updatedCosts }),
        }),
        {
          pending: "Saving payment...",
          success: "Payment saved.",
          error: {
            render({ data }) {
              return data?.response?.data?.message || "Failed to save payment.";
            },
          },
        }
      );

      setSelectedPayments((prev) => {
        const next = new Set(prev);
        if (amount - paid > 0) {
          next.add(paymentKey);
        } else {
          next.delete(paymentKey);
        }
        return next;
      });

      await refetchBooking();
      hidePayment();
    } catch (error) {
      console.error('Error updating booking costs:', error);
    }
  }

  const handleUpdateNote = async (newNote) => {
    try {
      await toast.promise(
        updateBooking({
          id: bookingId,
          bookingData: toBookingUpdateData(booking, { notes: newNote }),
        }),
        {
          pending: "Saving notes...",
          success: "Notes saved.",
          error: "Failed to save notes.",
        }
      );
      await refetchBooking();
      hideNote();
    } catch (error) {
      console.error('Error updating booking notes:', error);
    }
  }


  const handleDeleteBookingPayment = async (key) => {
    const paymentKey = String(key || "").trim();

    if (!paymentKey) {
      toast.error("Select a payment field to delete.");
      return;
    }

    const updatedCosts = { ...(booking.costs || {}) };
    delete updatedCosts[paymentKey];

    try {
      await toast.promise(
        updateBooking({
          id: bookingId,
          bookingData: toBookingUpdateData(booking, { costs: updatedCosts }),
        }),
        {
          pending: "Deleting payment...",
          success: "Payment deleted.",
          error: "Failed to delete payment.",
        }
      );

      setSelectedPayments((prev) => {
        const next = new Set(prev);
        next.delete(paymentKey);
        return next;
      });

      await refetchBooking();
      hidePayment();
    } catch (error) {
      console.error('Error updating booking costs:', error);
    }
  }




  const handleSaveSignature = async (sigUrl) => {
    if (!sigUrl) return;
  
    const updatedBooking = { ...booking };
    updatedBooking.signature = sigUrl;
    updatedBooking.signatureTimestamp = new Date().toISOString();
  
    try {
      // Optionally get the user's IP address
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      updatedBooking.signatureIp = data.ip;
    } catch (ipError) {
      console.warn('Could not retrieve IP address:', ipError);
      updatedBooking.signatureIp = 'unknown';
    }
  
    try {
      await toast.promise(
        updateBooking({
          id: bookingId,
          bookingData: toBookingUpdateData(booking, updatedBooking),
        }),
        {
          pending: "Saving contract signature...",
          success: "Contract signature saved.",
          error: "Failed to save contract signature.",
        }
      );
      await refetchBooking();
      hideContract();
    } catch (error) {
      console.error('Error updating booking signature:', error);
    }
  };
  

  const handUpdateCustomer = async (newCust) => {
    if (newCust) {
      try {
        const result = await updateCustomer({
          id: booking.customerId,
          customerData: newCust
        });
        console.log('Customer updated:', result);
      } catch (error) {
        console.error('Error updating customer:', error);
      }
    }

    hideCustomer();
  }


  // handle when a payment row is selected or a new payment is being added
  const handleClickEditPayment = (fieldName) => {
    if (fieldName && booking.costs?.[fieldName]) {
      setSelectedPaymentModalKey(fieldName);
      setSelectedPaymentModalData({ ...booking.costs[fieldName], name: fieldName });
    } else {
      setSelectedPaymentModalKey("");
      setSelectedPaymentModalData({});
    }
      setPaymentModalRenderKey((current) => current + 1);
      openPayment();
  }

  const handleOpenSavedCardConfirm = () => {
    const hasSelectedCharges = Array.from(selectedPayments).some((key) => {
      const cost = booking.costs?.[key];
      return cost && Number(cost.amount || 0) - Number(cost.paid || 0) > 0;
    });

    if (!hasSelectedCharges) {
      toast.error("Select at least one unpaid payment item first.");
      return;
    }

    openSavedCardConfirm();
  };

  const handlePosPayment = async () => {
    // datastring: booking=abc123;costs=[deposit:100,balance:250,fuel:30]
    let dataString = `booking=${bookingId};costs=[`;
    let totalAmount = 0;
    selectedPayments.forEach((key) => {
      const amount = booking.costs[key].amount - (booking.costs[key].paid || 0);
      totalAmount += parseFloat(amount);
      dataString += `${key}:${amount},`
    })

    dataString += "]"

    console.log("datastring", dataString);
    console.log("total", totalAmount);

    // Make sure the customer is in the Square Directory (creating them if needed)
    // so the sale can be linked to them (receipts + save-card attach to the
    // right profile). Best-effort: never block taking payment if this fails.
    let squareCustomerId;
    try {
      const result = await ensureSquareCustomer.mutateAsync(bookingId);
      squareCustomerId = result?.squareCustomerId;
    } catch (error) {
      console.error("Could not sync customer to Square before POS payment:", error);
      toast.warn("Couldn't sync customer to Square; you may need to enter receipt info manually.");
    }

    // TEMP DEBUG: confirm whether a Square customer id is actually being sent.
    console.log("POS customer_id being sent:", squareCustomerId);
    toast.info(
      squareCustomerId
        ? `Sending customer_id: ${squareCustomerId}`
        : "No customer_id — sale will NOT be linked (save-card button won't show)",
      { autoClose: 6000 }
    );

    startSquarePOSPayment(totalAmount*100, dataString, squareCustomerId)
  }

  const handleSavedCardPayment = async () => {
    const costKeys = Array.from(selectedPayments);

    if (costKeys.length === 0) {
      toast.error("Select at least one unpaid payment item first.");
      return;
    }

    try {
      await toast.promise(
        chargeSavedCard.mutateAsync({ id: bookingId, costKeys }),
        {
          pending: "Charging saved card...",
          success: "Saved card payment completed!",
          error: {
            render({ data }) {
              return data?.response?.data?.message || "Failed to charge saved card.";
            },
          },
        }
      );

      setSelectedPayments(new Set());
      closeSavedCardConfirm();
    } catch (error) {
      console.error("Saved card payment failed:", error);
    }
  }


  const activeChecklistTemplate = checklistTemplates.find((template) => template.active !== false) || checklistTemplates[0];
  const latestChecklist = bookingChecklists?.[0];
  const checklistCompletedAt = booking?.checklist?.completedAt || latestChecklist?.completedAt;
  const checklistSignerName = booking?.checklist?.signerName || latestChecklist?.signature?.signedByName || latestChecklist?.signer?.name;
  const checklistIsSaving = createBookingChecklist.isLoading || updateBookingChecklist.isLoading;
  const checklistUnavailable = checklistTemplatesIsLoading || !activeChecklistTemplate;


  if (bookingIsLoading) return <p>Loading booking...</p>;
  if (bookingError) return <p>Booking not found.</p>;

  const checkInUrl = (window.location.origin + `/${clientData?.id}/confirmation?booking_id=${bookingId}`);
  const reminderText = `Hello ${customer?.firstName}, this is a reminder of your upcoming rental with ${clientData?.companyName}. To speed up your check-in process and view arrival instructions, please use the link below, we look forward to seeing you soon! ${checkInUrl}`;
  const selectedPaymentBreakdown = Array.from(selectedPayments)
    .map((key) => {
      const cost = booking.costs?.[key] || {};
      const amount = Number(cost.amount || 0);
      const paid = Number(cost.paid || 0);
      const due = Math.max(amount - paid, 0);

      return {
        key,
        label: formatChargeLabel(key),
        amount,
        paid,
        due,
      };
    })
    .filter((item) => item.due > 0);
  const selectedPaymentTotal = selectedPaymentBreakdown.reduce((total, item) => total + item.due, 0);
  const customerAddress = helpers.formatCustomerAddress(customer);
  const customerDetails = getCustomerDetails(customer);

  return (
    <div className={`narrow-container ${styles.page}`}>

      <div className={styles.summaryCard}>
        <div className={styles.pageHeader}>
          <button className={styles.backButton} onClick={() => navigate(-1)}>
            <ArrowLeft size={18} aria-hidden="true" />
            Go Back
          </button>

          <div className={styles.titleBlock}>
            <span className={styles.kicker}>Booking Details</span>
            <h2>{helpers.formatCustomerName(customer, "Customer loading")}</h2>
            <a
                        href={customer?.phone ? `tel:${customer.phone}` : undefined}
                        className="dashboard-phone-link"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <Phone size={16} aria-hidden="true" />
                        {helpers.formatCustomerPhone(customer?.phone, "Phone not recorded")}
                      </a>
            <span className={styles.createdMeta}>
              <CalendarClock size={15} aria-hidden="true" />
              Created {formatTimestamp(booking.createdAt)}
            </span>
          </div>
        </div>

        <div className={styles.statusGrid}>
          <StatusCard
            icon={FileSignature}
            label="Contract"
            value={booking.signature ? "Signed - " + formatTimestamp(booking.signatureTimestamp) : "Not signed"}
            tone={booking.signature ? "good" : "bad"}
            onClick={openContract}
          />
          <StatusCard
            icon={ShieldCheck}
            label="License"
            value={booking.hasLicense ? "Checked - " + formatTimestamp(booking.hasLicenseTimestamp) : "Not checked"}
            tone={booking.hasLicense ? "good" : "bad"}
            onClick={openLicenseModal}
          />
          <StatusCard
            icon={ClipboardCheck}
            label="Checklist"
            value={checklistCompletedAt ? "Completed - " + formatTimestamp(checklistCompletedAt) : "Not completed"}
            tone={checklistCompletedAt ? "good" : "bad"}
            onClick={openChecklist}
            disabled={checklistUnavailable}
          />
          <StatusCard
            icon={Clock}
            label="Arrival Time"
            value={booking.arrivalTime ? `${booking.arrivalTime} confirmed` : "pending"}
            tone={booking.arrivalTime ? "good" : "bad"}
            onClick={openArrivalModal}
          />
        </div>

        <div className={styles.detailGrid}>
          <div className={styles.detailBox}>
            <div>
              <span className={styles.detailLabel}>Internal Notes</span>
              <strong>{booking.notes || "None"}</strong>
            </div>
            <button className={styles.inlineAction} onClick={openNote}>
              <MessageSquare size={15} aria-hidden="true" />
              Edit
            </button>
          </div>

          <div className={styles.detailBox}>
            <div>
              <span className={styles.detailLabel}>Customer Notes</span>
              <strong>{booking.customerNotes || "None"}</strong>
            </div>
          </div>

          {(checklistTemplatesError || bookingChecklistsError) && (
            <div className={styles.inlineError}>
              {checklistTemplatesError && "Checklist template could not be loaded."}
              {bookingChecklistsError && " Saved checklists could not be loaded."}
            </div>
          )}
        </div>

        <div className={styles.actionBar}>
          <button className={styles.actionButton} onClick={() => openEmailModal()}>
            <Send size={16} aria-hidden="true" />
            Email Options
          </button>
          <button className={styles.actionButton} onClick={() => openLinkModal()}>
            <LinkIcon size={16} aria-hidden="true" />
            Link Options
          </button>
          <button className={styles.actionButton} onClick={() => {
            navigator.clipboard.writeText(reminderText);
            toast.success("Reminder text copied to clipboard");
          }}>
            <Mail size={16} aria-hidden="true" />
            Copy Reminder Text
          </button>
        </div>
      </div>

      <div className={styles.legacyTop}>
        <button className="btn" onClick={() => navigate(-1)}>Go Back</button>
        <h2>Booking Details</h2>
        <p><strong>Created On: </strong> {`✅ ${new Date(booking.createdAt)}`}</p>
        <p><strong>Contract Signed: </strong>{booking.signature ? `✅ ${booking.signatureTimestamp ? new Date((booking.signatureTimestamp)) : "YES" }` : "❌ NO"}</p>
        <p><strong>License Checked: </strong>{booking.hasLicense ? `✅ ${new Date((booking.hasLicenseTimestamp))}` : "❌ NO"}</p>
        <div className="info-edit-box d-flex align-items-center justify-content-between mt-3 p-3 bg-light rounded border">
        <div>
          <strong>Arrival Time:</strong><span className={styles.textBox}>{" "}
          {booking.arrivalTime ? `${booking.arrivalTime} (confirmed)` : "10:00 AM (pending)"}
          </span>
        </div>
          <button className="btn btn-sm btn-outline-primary" onClick={openArrivalModal}>
            Edit
          </button>
        </div>

        <div className="info-edit-box d-flex align-items-center justify-content-between mt-3 p-3 bg-light rounded border">
          <div >
            <strong>Notes:</strong><span className={styles.textBox}>{booking.notes || ""}</span>
          </div>
          <button className="btn btn-sm btn-outline-primary" onClick={openNote}>
            Edit
          </button>
        </div>

        <div className="info-edit-box d-flex align-items-center justify-content-between mt-3 p-3 bg-light rounded border">
          <div>
            <strong>Customer Notes:</strong><span className={styles.textBox}>{booking.customerNotes || ""}</span>
          </div>
        </div>

        <div className="info-edit-box d-flex align-items-center justify-content-between mt-3 p-3 bg-light rounded border">
          <div>
            <strong>Checklist:</strong><span className={styles.textBox}>{" "}
            {checklistCompletedAt
              ? `✅ Completed ${new Date(checklistCompletedAt).toLocaleDateString()}${checklistSignerName ? ` by ${checklistSignerName}` : ""}`
              : "❌ Not completed"}
            </span>
            {checklistTemplatesError && <div className="text-danger">Checklist template could not be loaded.</div>}
            {bookingChecklistsError && <div className="text-danger">Saved checklists could not be loaded.</div>}
          </div>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={openChecklist}
            disabled={checklistUnavailable}
          >
            {checklistTemplatesIsLoading || bookingChecklistsIsLoading
              ? "Loading..."
              : checklistCompletedAt ? "View/Edit" : "Complete"}
          </button>
        </div>


  


      <div className="d-flex flex-wrap flex-md-row flex-column justify-content-stretch mt-3">
        <button className="btn p-2 m-2" onClick={() => openContract()}>Contract</button>
        <button className="btn p-2 m-2" onClick={() => openEmailModal()}>Email Options</button>
        <button className="btn p-2 m-2" onClick={() => openLinkModal()}>Link Options</button>
        <button className="btn p-2 m-2" onClick={() => {
         navigator.clipboard.writeText(reminderText);
          toast.success("Reminder text copied to clipboard");
        }
        }>
          Copy Reminder Text
        </button>
      </div>
      </div>
      
        
      {(
        <div className={styles.sections}>

          <div className={`${styles.section} main-border`}>
            <div className={styles.sectionHeader}>
              <h3><CalendarClock size={18} aria-hidden="true" /> Slots</h3>
              <div>
                <button className="btn" onClick={() => openSlotModal()}>
                      <Plus size={15} aria-hidden="true" /> Add Slot
                </button>
                <button className="btn" onClick={() => setEditSlots(!editSlots)}>
                      <Pencil size={15} aria-hidden="true" /> Edit
                </button>
              </div>

            </div>
            <div className="table-container table-responsive">
              <table className="table">
                  <thead>
                    <tr>
                        <th className={styles.tableImage}>Image</th>
                        <th>Unit</th>
                        <th>Date</th>
                        <th>Time</th>
                        <th>Price</th>
                    </tr>
                  </thead>
                    <tbody>
                    {booking.slots && booking.slots.map((slot) => {
                      return (
                        <tr key={slot.id}>
                        <SlotTableRow slotId={slot.id} onSelect={() => console.log("slot clicked")} isSelected={false}/>
                        {editSlots && <td>
                          <button onClick={() => handleRemoveSlot(slot)} className="btn btn-danger"><FaTrash /></button>
                        </td>}
                        </tr>
                      )
                    })}
      
                    </tbody>
                </table>

                
            </div>
          </div>


          <div className={`${styles.section} main-border`}>
            <div className={styles.sectionHeader}>
              <h3><CreditCard size={18} aria-hidden="true" /> Payments</h3>
              <div>
                <button className="btn" onClick={() => handleClickEditPayment()}>
                      <Plus size={15} aria-hidden="true" /> Add Field
                </button>
                {hasCardOnFile && selectedPayments.size > 0 && (
                  <button className="btn" onClick={() => handleOpenSavedCardConfirm()}>
                        <CreditCard size={15} aria-hidden="true" /> Charge Saved Card
                  </button>
                )}
                {selectedPayments.size > 0 && (
                  <button className="btn" onClick={() => handlePosPayment()}>
                        <CreditCard size={15} aria-hidden="true" /> Make In-Person Payment
                  </button>
                )}
              </div>

            </div>
            <div className="table-container table-responsive">
              <table className="table">
                  <thead>
                    <tr>
                        <th>Name</th>
                        <th>Total</th>
                        <th>Paid</th>
                        <th>Type</th>
                        <th>Make Payment</th>
                        <th>Edit</th>
                    </tr>
                  </thead>
                    <tbody>
                    {Object.entries(booking.costs).map(([key,value]) => {

                      return (
                        <tr key={key}>
                          <td>{key}</td>
                          <td>${value.amount ?? 0}</td>
                          <td>${value.paid ?? 0}</td>
                          <td>{value.payment?.type || "none"}</td>
                          <td>
                            { value.amount-value.paid > 0 && <input
                              type="checkbox"
                              checked={selectedPayments.has(key)}
                              onChange={() => {
                                setSelectedPayments((prev) => {
                                  const newSet = new Set(prev);
                                  if (newSet.has(key)) {
                                    newSet.delete(key);
                                  } else {
                                    newSet.add(key);
                                  }
                                  return newSet;
                                });
                              }}
                              onClick={(e) => e.stopPropagation()} // Prevent row click triggering checkbox twice
                            /> }
                          </td>
                          <td><button className="btn" onClick={() => handleClickEditPayment(key)}>Edit</button></td>
                        </tr>
                      )
                    })}
      
                    </tbody>
                </table>

                
            </div>
          </div>


          {Object.keys(booking.discounts).length > 0 && (
          <div className={`${styles.section} main-border`}>
            <div className={styles.sectionHeader}>
              <h3><ClipboardCheck size={18} aria-hidden="true" /> Discounts</h3>
            </div>
            <div className="table-container">
              <table className="table">
                <thead>
                <tr>
                    <th>Code</th>
                    <th>Amount</th>
                </tr>
                </thead>
                <tbody>
                {Object.entries(booking.discounts).map(([key, value]) => (
                  <tr key={key}>
                    <td>{key}</td>
                    <td>{value}</td>
                  </tr>
                ))}

                </tbody>
              </table>
            </div>
          </div>
          )}

          <div className={`${styles.section} main-border`}>
            <div className={styles.sectionHeader}>
                  <h3><User size={18} aria-hidden="true" /> Customer</h3>
                  <div>
                    <button className="btn" onClick={() => openCustomer()}>
                          <Pencil size={15} aria-hidden="true" /> Edit
                    </button>
                  </div>

                </div>
              {customerIsLoading && <p>Loading Customer...</p>}
              {customerError && <p>Customer Not Found.</p>}
              {customer && (
                <div className={styles.customerCard}>
                  <div className={styles.customerPrimary}>
                    <div className={styles.customerAvatar} aria-hidden="true">
                      <User size={22} />
                    </div>
                    <div className={styles.customerIdentity}>
                      <span>Customer</span>
                      <strong>{helpers.formatCustomerName(customer, "Customer")}</strong>
                      <div className={styles.customerContactLinks}>
                        {customer.phone && (
                          <a href={`tel:${customer.phone}`}>
                            <Phone size={15} aria-hidden="true" />
                            {helpers.formatCustomerPhone(customer.phone)}
                          </a>
                        )}
                        {customer.email && (
                          <a href={`mailto:${customer.email}`}>
                            <Mail size={15} aria-hidden="true" />
                            {customer.email}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={styles.customerInfoGrid}>
                    <div className={styles.customerInfoItem}>
                      <span>
                        <MapPin size={14} aria-hidden="true" />
                        Address
                      </span>
                      <strong>{customerAddress || "Not recorded"}</strong>
                    </div>

                    {customerDetails.map(([key, value]) => (
                      <div className={styles.customerInfoItem} key={key}>
                        <span>{formatChargeLabel(key)}</span>
                        <strong>{formatCustomerValue(value)}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
      )}

      

      <SlotModal isOpen={showSlotModal} onClose={() => hideSlotModal()} onSave={handleAddSlot} />
      
      <EditModal
        title="Contract"
        show={showContract}
        onClose={() => hideContract()}
        size="xl"
        fullscreen="md-down"
        dialogClassName="contract-modal-dialog"
        bodyClassName="contract-modal-body"
      >
        <ContractModalContent
          bookingId={bookingId}
          booking={booking}
          onSignatureSubmit={handleSaveSignature}
        />
      </EditModal>
      
      
      <EditModal title="Customer" show={showCustomer} onClose={() => hideCustomer()}>
        <CustomerForm initialData={customer} onSubmit={(newCust) => handUpdateCustomer(newCust)} />
      </EditModal>
      
      
      <EditModal title="Payment" show={showPayment} onClose={() => hidePayment()}>
        <ExtraForm
          key={`${selectedPaymentModalKey || "new"}-${paymentModalRenderKey}`}
          initialData={selectedPaymentModalData}
          onSubmit={(newData) => handleUpdateBookingPayment(newData, newData?.name)}
          onDelete={(key) => handleDeleteBookingPayment(key)}
        />
      </EditModal>

      <EditModal
        title="Confirm Saved Card Charge"
        show={showSavedCardConfirm}
        onClose={closeSavedCardConfirm}
      >
        <div className={styles.savedCardConfirm}>
          <div className={styles.confirmCustomer}>
            <span>Customer</span>
            <strong>{helpers.formatCustomerName(customer, "Customer")}</strong>
          </div>

          {customerCards?.[0] && (
            <div className={styles.confirmCustomer}>
              <span>Card</span>
              <strong>
                {customerCards[0].cardBrand ? `${customerCards[0].cardBrand} ` : ""}
                ending in {customerCards[0].last4}
              </strong>
            </div>
          )}

          <div className={styles.confirmBreakdown}>
            <div className={styles.confirmBreakdownHeader}>
              <span>Charge</span>
              <span>Total</span>
              <span>Paid</span>
              <span>Charge Now</span>
            </div>
            {selectedPaymentBreakdown.map((item) => (
              <div className={styles.confirmBreakdownRow} key={item.key}>
                <span>{item.label}</span>
                <span>{formatMoney(item.amount)}</span>
                <span>{formatMoney(item.paid)}</span>
                <strong>{formatMoney(item.due)}</strong>
              </div>
            ))}
          </div>

          <div className={styles.confirmTotal}>
            <span>Total to charge saved card</span>
            <strong>{formatMoney(selectedPaymentTotal)}</strong>
          </div>

          <div className={styles.confirmActions}>
            <button className="btn" onClick={closeSavedCardConfirm}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSavedCardPayment}
              disabled={chargeSavedCard.isLoading || selectedPaymentTotal <= 0}
            >
              {chargeSavedCard.isLoading ? "Charging..." : `Confirm Charge ${formatMoney(selectedPaymentTotal)}`}
            </button>
          </div>
        </div>
      </EditModal>

      
      <EditModal title="Notes" show={showNote} onClose={() => hideNote()}>
        <NotesForm 
            initialData={booking.notes}
            onSubmit={(newData) => handleUpdateNote(newData)} />
      </EditModal>
      
      
      <EditModal
          title="Set Desired Arrival Time"
          show={showArrivalModal}
          onClose={closeArrivalModal}
        >
          <ArrivalTimeForm
            initialData={booking.arrivalTime || "10:00"}
            min={"09:45"}
            max={"20:00"}
            onSubmit={handleUpdateArrival}
          />
      </EditModal>

      <EditModal
        title="Confirm Operator License"
        show={showLicenseModal}
        onClose={closeLicenseModal}
      >
        <LicenseForm
          initialLicense={booking.hasLicense ?? false}
          onSubmit={handleSaveLicense}
        />
      </EditModal>
        

      <EditModal title="Send Emails" show={showEmailModal} onClose={() => hideEmailModal()}>
        <div className="d-flex flex-column">
          <button className="btn p-2 m-2" onClick={() => handleSendConfimrationEmail()}>Email Confirmation</button>
          <button className="btn p-2 m-2" onClick={() => handleSendCheckinEmail()}>Email Check-In</button>
          <button className="btn p-2 m-2" onClick={() => handleSendReviewRequestEmail()}>Email Review Request</button>
          <button className="btn p-2 m-2" onClick={() => handleSendContractEmail()}>Email Contracts</button>
          <button className="btn p-2 m-2" onClick={() => handleSendLicenseRequirementEmail()}>Email License Requirement</button>
        
        </div>
      </EditModal>

      
      <EditModal title="Copy Links to Clipboard" show={showLinkModal} onClose={() => hideLinkModal()}>
        <div className="d-flex flex-column">
          <button className="btn p-2 m-2" onClick={() => handleCopyText(checkInUrl)}>Check-In Link</button>
          <button className="btn p-2 m-2" onClick={() => handleCopyText(clientData.reviewUrl)}>Review Link</button>
        </div>
      </EditModal>
      <EditModal
        title="Checklist"
        show={showChecklist}
        onClose={() => hideChecklist()}
        size="xl"
        dialogClassName="checklist-modal-dialog"
        bodyClassName="checklist-modal-body"
      >
        <ChecklistForm
          template={activeChecklistTemplate}
          existingChecklist={latestChecklist}
          customer={customer}
          onSubmit={handleChecklistSubmit}
          onCancel={hideChecklist}
          isSubmitting={checklistIsSaving}
        />
      </EditModal>
    </div>
  );
};

export default BookingDetailsPage;
